import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { QueryParameters as ISelfServeBrandOrders } from "@faire/web-api--source/api/help-center/self-serve-brand-orders/get";
import { useQuery as useSelfServeBrandOrdersQuery } from "@faire/web-api--source/api/help-center/self-serve-brand-orders/get-hooks";
import { trackHelpCenterOrderShowMoreClick } from "@faire/web-api--source/events/helpCenter/click/orderShowMore";
import { ISelfServeBrandOrder } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeBrandOrder";
import { useEffect, useState } from "react";

import { BRAND_ORDERS_PAGE_SIZE } from "../consts";

import { useIssueType } from "./useIssueType";

export type PaginatedBrandOrderData = {
  isLoading: boolean;
  brandOrders: ISelfServeBrandOrder[] | undefined;
  hasMoreOrders: () => boolean;
  showMoreOrders: () => void;
  dataUpdatedAt: number;
};

/**
 *  This hook is used to fetch brand orders for the self-serve order select page.
 * @returns PaginatedBrandOrderData
 */
export const usePaginatedBrandOrders = (): PaginatedBrandOrderData => {
  const [currentPage, setCurrentPage] = useState(1);
  const [brandOrders, setBrandOrders] = useState<
    ISelfServeBrandOrder[] | undefined
  >(undefined);

  const retailerToken = getRetailerToken() ?? "";
  const brandToken = getBrandToken() ?? "";
  const { issueType } = useIssueType();

  const {
    data: brandOrderResponse,
    isLoading,
    refetch,
    dataUpdatedAt,
  } = useSelfServeBrandOrdersQuery(
    ISelfServeBrandOrders.build({
      page: currentPage,
      pageSize: BRAND_ORDERS_PAGE_SIZE,
    })
  );

  const totalPages = brandOrderResponse?.pagination_data?.page_count ?? 1;

  const hasMoreOrders = () => {
    return currentPage < totalPages;
  };

  const showMoreOrders = () => {
    trackHelpCenterOrderShowMoreClick(retailerToken, brandToken, issueType);
    if (hasMoreOrders()) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    refetch().then((response) => {
      const nextPageOrders = response.data?.brand_orders;
      if (nextPageOrders) {
        setBrandOrders((prevBrandOrders) => [
          ...(prevBrandOrders ?? []),
          ...nextPageOrders,
        ]);
      }
    });
  }, [refetch, currentPage]);

  return {
    isLoading,
    brandOrders,
    hasMoreOrders,
    showMoreOrders,
    dataUpdatedAt,
  };
};
